/**
 * PhraseForm Component
 * Form for adding or editing a favorite phrase.
 * Fields: label, source text, target language (optional).
 */

import React, { useState } from 'react';
import { FavoritePhrase } from '../../types';
import './PhraseForm.css';

export interface PhraseFormData {
  label: string;
  text: string;
  targetLang?: string;
  tags?: string[];
}

interface PhraseFormProps {
  phrase?: FavoritePhrase;
  onSubmit: (data: PhraseFormData) => Promise<void>;
  onCancel: () => void;
}

const COMMON_LANGUAGES = [
  { code: '', name: 'Any / Not specified' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'he', name: 'Hebrew' },
  { code: 'ar', name: 'Arabic' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ru', name: 'Russian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
];

export const PhraseForm: React.FC<PhraseFormProps> = ({
  phrase,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<PhraseFormData>({
    label: phrase?.label || '',
    text: phrase?.text || '',
    targetLang: phrase?.targetLang || '',
    tags: phrase?.tags || [],
  });

  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.label.trim()) {
      setError('Label is required');
      return;
    }

    if (!formData.text.trim()) {
      setError('Phrase text is required');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        ...formData,
        targetLang: formData.targetLang || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save phrase');
      setIsSubmitting(false);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags?.includes(tag)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tag],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: (prev.tags || []).filter((t) => t !== tagToRemove),
    }));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <form className="phrase-form" onSubmit={handleSubmit}>
      <h3>{phrase ? 'Edit Phrase' : 'Add Favorite Phrase'}</h3>

      {error && (
        <div className="phrase-form__error" role="alert">
          {error}
        </div>
      )}

      <div className="phrase-form__field">
        <label htmlFor="phrase-label" className="phrase-form__label">
          Label *
        </label>
        <input
          id="phrase-label"
          type="text"
          className="phrase-form__input"
          value={formData.label}
          onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
          placeholder="e.g., Meeting intro, Greeting, Sign-off"
          maxLength={100}
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="phrase-form__field">
        <label htmlFor="phrase-text" className="phrase-form__label">
          Phrase Text *
        </label>
        <textarea
          id="phrase-text"
          className="phrase-form__textarea"
          value={formData.text}
          onChange={(e) => setFormData((prev) => ({ ...prev, text: e.target.value }))}
          placeholder="The phrase you want to translate frequently"
          rows={3}
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="phrase-form__field">
        <label htmlFor="phrase-target-lang" className="phrase-form__label">
          Target Language
        </label>
        <select
          id="phrase-target-lang"
          className="phrase-form__select"
          value={formData.targetLang}
          onChange={(e) => setFormData((prev) => ({ ...prev, targetLang: e.target.value }))}
          disabled={isSubmitting}
        >
          {COMMON_LANGUAGES.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>

      <div className="phrase-form__field">
        <label className="phrase-form__label">Tags</label>
        <div className="phrase-form__tag-input-row">
          <input
            type="text"
            className="phrase-form__input"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            placeholder="Add a tag and press Enter"
            disabled={isSubmitting}
          />
          <button
            type="button"
            className="phrase-form__tag-add-btn"
            onClick={handleAddTag}
            disabled={isSubmitting || !tagInput.trim()}
          >
            Add
          </button>
        </div>
        {formData.tags && formData.tags.length > 0 && (
          <div className="phrase-form__tags">
            {formData.tags.map((tag) => (
              <span key={tag} className="phrase-form__tag">
                {tag}
                <button
                  type="button"
                  className="phrase-form__tag-remove"
                  onClick={() => handleRemoveTag(tag)}
                  aria-label={`Remove tag ${tag}`}
                  disabled={isSubmitting}
                >
                  x
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="phrase-form__actions">
        <button
          type="button"
          className="phrase-form__btn phrase-form__btn--cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="phrase-form__btn phrase-form__btn--submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : phrase ? 'Update Phrase' : 'Add Phrase'}
        </button>
      </div>
    </form>
  );
};
